import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { useData } from "@/lib/useData";
import type { Contact, Company } from "@/lib/types";
import { Plus, MoreHorizontal, Pencil, Trash2, Search } from "lucide-react";

const emptyContact = {
	firstName: "",
	lastName: "",
	email: "",
	phone: "",
	title: "",
	companyId: null as string | null,
};

export function ContactsPage() {
	const contactsFetcher = useCallback(() => api.contacts.list(), []);
	const companiesFetcher = useCallback(() => api.companies.list(), []);

	const { data: contacts, loading, refresh } = useData<Contact[]>(contactsFetcher);
	const { data: companies } = useData<Company[]>(companiesFetcher);

	const [search, setSearch] = useState("");
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<Contact | null>(null);
	const [form, setForm] = useState(emptyContact);

	const companyMap = new Map(companies?.map(c => [c.id, c.name]) ?? []);

	const filtered = contacts?.filter(c => {
		const q = search.toLowerCase();
		return (
			c.firstName.toLowerCase().includes(q) ||
			c.lastName.toLowerCase().includes(q) ||
			c.email.toLowerCase().includes(q) ||
			c.title.toLowerCase().includes(q)
		);
	}) ?? [];

	function openCreate() {
		setEditing(null);
		setForm(emptyContact);
		setDialogOpen(true);
	}

	function openEdit(contact: Contact) {
		setEditing(contact);
		setForm({
			firstName: contact.firstName,
			lastName: contact.lastName,
			email: contact.email,
			phone: contact.phone,
			title: contact.title,
			companyId: contact.companyId,
		});
		setDialogOpen(true);
	}

	async function handleSave() {
		if (editing) {
			await api.contacts.update(editing.id, form);
		} else {
			await api.contacts.create(form);
		}
		setDialogOpen(false);
		refresh();
	}

	async function handleDelete(id: string) {
		await api.contacts.delete(id);
		refresh();
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Contacts</h1>
					<p className="text-sm text-muted-foreground">{contacts?.length ?? 0} total contacts</p>
				</div>
				<Button onClick={openCreate} size="sm">
					<Plus className="h-4 w-4 mr-1" /> Add Contact
				</Button>
			</div>

			<Card>
				<CardHeader className="pb-3">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
						<Input
							placeholder="Search contacts..."
							value={search}
							onChange={e => setSearch(e.target.value)}
							className="pl-9"
						/>
					</div>
				</CardHeader>
				<CardContent className="p-0">
					{loading ? (
						<div className="p-6 text-center text-muted-foreground">Loading...</div>
					) : filtered.length === 0 ? (
						<div className="p-6 text-center text-muted-foreground">No contacts found</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Name</TableHead>
									<TableHead>Title</TableHead>
									<TableHead>Email</TableHead>
									<TableHead>Phone</TableHead>
									<TableHead>Company</TableHead>
									<TableHead className="w-10"></TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filtered.map(contact => (
									<TableRow key={contact.id}>
										<TableCell className="font-medium">
											{contact.firstName} {contact.lastName}
										</TableCell>
										<TableCell>{contact.title}</TableCell>
										<TableCell className="text-muted-foreground">{contact.email}</TableCell>
										<TableCell className="text-muted-foreground">{contact.phone}</TableCell>
										<TableCell>{companyMap.get(contact.companyId ?? "") ?? "—"}</TableCell>
										<TableCell>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon" className="h-8 w-8">
														<MoreHorizontal className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem onClick={() => openEdit(contact)}>
														<Pencil className="h-4 w-4 mr-2" /> Edit
													</DropdownMenuItem>
													<DropdownMenuItem className="text-destructive" onClick={() => handleDelete(contact.id)}>
														<Trash2 className="h-4 w-4 mr-2" /> Delete
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			{/* Create/Edit Dialog */}
			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{editing ? "Edit Contact" : "New Contact"}</DialogTitle>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>First Name</Label>
								<Input value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
							</div>
							<div className="space-y-2">
								<Label>Last Name</Label>
								<Input value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
							</div>
						</div>
						<div className="space-y-2">
							<Label>Title</Label>
							<Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Email</Label>
							<Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Phone</Label>
							<Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
						</div>
						<div className="space-y-2">
							<Label>Company</Label>
							<Select
								value={form.companyId ?? "none"}
								onValueChange={v => setForm({ ...form, companyId: v === "none" ? null : v })}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select company" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="none">None</SelectItem>
									{companies?.map(c => (
										<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
						<Button onClick={handleSave}>{editing ? "Save Changes" : "Create Contact"}</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
